import { motion } from 'framer-motion';
import { AlertTriangle, ChevronRight } from 'lucide-react';

/**
 * Home-screen banner: shows if any supplement in the user's stack is <7 days
 * from running out. Renders nothing when the alerts array is empty.
 */
export default function RestockAlertBanner({ alerts, onClick }) {
  if (!Array.isArray(alerts) || alerts.length === 0) return null;

  const soonest = [...alerts].sort(
    (a, b) => (a.days_until_restock ?? 0) - (b.days_until_restock ?? 0)
  )[0];
  const extra = alerts.length - 1;
  const isEmpty = (soonest.days_until_restock ?? 0) <= 0;

  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`w-full text-left p-4 rounded-2xl border transition-colors group ${
        isEmpty
          ? 'bg-red-50 border-red-200 hover:border-red-300'
          : 'bg-amber-50 border-amber-200 hover:border-amber-300'
      }`}
      data-testid="restock-alert-banner"
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isEmpty ? 'bg-red-100' : 'bg-amber-100'
        }`}>
          <AlertTriangle className={`w-4 h-4 ${isEmpty ? 'text-red-600' : 'text-amber-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${
            isEmpty ? 'text-red-700' : 'text-amber-700'
          }`}>
            {isEmpty ? 'Restock now' : 'Running low'}
          </p>
          <p className="font-semibold text-sm text-[#1A1A1A] truncate">
            {soonest.name}
            {extra > 0 && (
              <span className="text-xs font-normal text-muted-foreground ml-2">
                +{extra} more
              </span>
            )}
          </p>
          <p className={`text-xs mt-0.5 ${isEmpty ? 'text-red-700/80' : 'text-amber-700/80'}`}>
            {isEmpty
              ? 'Your stash may already be empty. Reorder when you can.'
              : `~${soonest.days_until_restock} day${soonest.days_until_restock === 1 ? '' : 's'} left at your current pace`}
          </p>
        </div>
        <ChevronRight className={`w-4 h-4 group-hover:translate-x-0.5 transition-transform flex-shrink-0 mt-2 ${
          isEmpty ? 'text-red-500' : 'text-amber-500'
        }`} />
      </div>
    </motion.button>
  );
}
